/**
 * PageFilterSorter - Utility for filtering and sorting pages by index
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for filtering pages by page index range
 * and sorting them, which is useful for pagination and visible page calculations.
 */

import type { PageLayout } from '../../../core/types/Typesetter';

/**
 * Filter pages by page index range and sort by index
 * 
 * Filters pages to include only those within the specified range (inclusive)
 * and sorts them by page_index in ascending order.
 * 
 * @param pages - Array of PageLayout objects
 * @param startPage - Starting page index (inclusive)
 * @param endPage - Ending page index (inclusive)
 * @returns Filtered and sorted array of pages
 */
export function filterAndSortPagesByRange(
  pages: PageLayout[],
  startPage: number,
  endPage: number
): PageLayout[] {
  return pages
    .filter(p => p.page_index >= startPage && p.page_index <= endPage)
    .sort((a, b) => a.page_index - b.page_index);
}

/**
 * Filter pages by page index range (without sorting)
 * 
 * Filters pages to include only those within the specified range (inclusive).
 * 
 * @param pages - Array of PageLayout objects
 * @param startPage - Starting page index (inclusive)
 * @param endPage - Ending page index (inclusive)
 * @returns Filtered array of pages
 */
export function filterPagesByRange(
  pages: PageLayout[],
  startPage: number,
  endPage: number
): PageLayout[] {
  return pages.filter(p => p.page_index >= startPage && p.page_index <= endPage);
}

/**
 * Sort pages by page index
 * 
 * Sorts pages by page_index in ascending order.
 * 
 * @param pages - Array of PageLayout objects
 * @returns Sorted array of pages
 */
export function sortPagesByIndex(pages: PageLayout[]): PageLayout[] {
  return [...pages].sort((a, b) => a.page_index - b.page_index);
}

